/**
 * `TimelineRunner` — the schedule and the state machine (ARCHITECTURE.md §8.1).
 *
 * Two halves live here, and the split is what makes the player seekable:
 *
 * 1. **The pure reducer.** `stageStateAt(scene, ctx, t)` replays every cue
 *    with `t_cue ≤ t` at its clamped local time `apply(cue, tLocal)` from the
 *    scene's `initial` state. It reads no wall clock, no renderer and no
 *    previous frame, so `seek(t)` and "play from 0 to t" produce the *same*
 *    object — that is the determinism contract, and `runner.test.ts` asserts
 *    it frame by frame.
 * 2. **The scheduler.** `TimelineRunner` advances a `StoryClock`, freezes it
 *    at `checkpoint` cues (§8.1 holds), fires cue *effects* (audio lines,
 *    sfx, music) exactly once on the way forward, and implements the tap
 *    policy of §8.6 and the hold/idle/nudge timers.
 *
 * Nothing in this file imports pixi or touches the DOM: the whole runner runs
 * headless in vitest. Rendering is `world.ts`, which consumes a `StageState`.
 *
 * Wall clock is used in exactly three places, all of them presentation and
 * none of them in the scene graph: the 300 ms tap debounce, the idle-dim
 * timer (§8.6) and the tap-gate nudges (ADDENDUM §5.3).
 *
 * **Scene transitions** (SKEL-STORY "Scene transitions", `transition.ts`) are story time too. A
 * play-through into the next scene starts its clock at `−lead` — a pre-roll in which no cue fires —
 * so the transition's covered midpoint lands on the scene's t = 0 and the first line is voiced
 * there, never over the old picture. The story's `ending` runs the clock past the last scene's
 * `duration` before `ended`. Seeks and scene jumps are cuts: they land at their `t` as ever.
 *
 * **A story still being made** (docs/STORY-STREAMING.md, `stream.ts`) grows while it plays. While
 * the player says more parts will come (`setStreamOpen(true)`), the end of the last ready part is
 * not the end: the runner enters `waiting` — the clock stopped on the last frame, nothing voiced —
 * and `scenesAppended()` re-enters the ordinary play-through into the next part, transition and
 * all. A stream that closes while the child waits goes on to whatever arrived, or ends there.
 */

import type { Action, Bundle, BundleScene } from '@/schema/story'

import { cameraCut, interpCamera } from './camera'
import type { CameraFrame, CameraShot } from './camera'
import { StoryClock } from './clock'
import type { AudioMaster } from './clock'
import type { AudioPort, PlayerMode } from './index'
import { lineFeeling } from './liveliness'
import { finalPose } from './motion'
import type { MotionPose, MotionState } from './motion'
import { mirrorFor, viewFacingFor } from './puppet'
import type { SocketName } from './puppet'
import { resolveSpriteClip, spriteClipFacing } from './sprite'
import { endingFor, prerollOf, transitionFor } from './transition'
import type { SceneEntry } from './transition'

// The facing rule lives with the puppet maths; re-exported so existing imports keep working.
export { authoredSide, mirrorFor } from './puppet'
export type { FacingRef } from './puppet'

/* ------------------------------------------------------------------ types */

/**
 * A baked cue: §4.6.4's `Exclude<Action, {op:'par'}> & {t: number}`.
 *
 * `schema/story.ts` exports a `Cue` alias too, but its `withT()` helper is
 * generic over `z.ZodObject` with no shape parameter, so `z.infer` widens the
 * whole union to `Record<string, unknown>` and `cue.op` never narrows. The
 * union is rebuilt here from `Action`, which infers correctly, and
 * `cuesOf()` is the one cast at the boundary. (Noted in docs/WIRING.md for
 * the schema owner.)
 */
export type Cue = Exclude<Action, { op: 'par' }> & { t: number }

/** The scene's baked cues, correctly typed. */
export function cuesOf(scene: BundleScene): Cue[] {
  return scene.cues as unknown as Cue[]
}

/** Hold kinds. `tap_gate` is ADDENDUM §5.3 and is scheduled here already. */
export type HoldKind = 'scene_end' | 'question' | 'tap_gate'

/**
 * §8.1 visible phases. `holding` covers `scene_end` and `tap_gate`; a question hold is its own phase.
 * `waiting`: the end of the last part that is ready, while the story is still being made.
 */
export type Phase = 'playing' | 'holding' | 'question' | 'paused' | 'waiting' | 'ended'

export type TapSource = 'stage' | 'hud'

/** What `onTap` did — the HUD uses it for the "nothing happened" feel, tests assert the policy table. */
export type TapResult = 'accepted' | 'swallowed' | 'queued' | 'debounced' | 'ignored' | 'undimmed'

/** A checkpoint cue with the ADDENDUM's extra kind and `verb_cue` field, read defensively. */
type CheckpointCue = Extract<Cue, { op: 'checkpoint' }> & { kind: HoldKind; verb_cue?: string }

/** A narration/dialogue/question cue — the three "line" kinds (§8.4). */
export type LineCue = Extract<Cue, { op: 'narrate' | 'say' | 'question' }>

export interface ActorState {
  /** A validated event's body motion is evidence, not optional decoration. */
  essentialMotion?: boolean
  handle: string
  /** Feet position, percent of the 1920×1080 stage. */
  pos: [number, number]
  /**
   * The direction the character looks **on screen**: `+1` right, `−1` left.
   * Not "as authored" — a puppet's side view may be drawn facing either way,
   * and a front view never turns, so how to draw this is `mirror`'s job.
   */
  facing: 1 | -1
  /**
   * Draw the view the current action binds to flipped around the feet.
   * Derived, never set by a cue: `mirrorFor(facing, view the action binds)`,
   * recomputed on every snapshot, so an action change (front idle → side
   * walk) or a seek can never leave a stale flip behind.
   */
  mirror: boolean
  visible: boolean
  alpha: number
  /** Story action key (`idle`, `move`, `talk`, `wave`…), resolved through `cast[].puppet.actions`. */
  clip: string
  /** Scene-local seconds at which the current clip started — the animation's zero. */
  clipStartedAt: number
  /** The action shown before `clip`, for the 280 ms crossfade (Q9a); null when there was none. */
  prevClip: string | null
  /** Scene-local seconds at which `prevClip` started. */
  prevClipStartedAt: number
  emotion: string | null
  /** Prop handle this actor is carrying. */
  holding: string | null
  /** True while this actor's own `say` line is playing (drives the talk overlay). */
  speaking: boolean
  /** Q9(c): scene second at which `facing` last changed — the 180 ms turn. −∞ = never. */
  facingSince: number
  /** Q6: the masked upper-body layer (a `hold` pose over a walk), or null. */
  layer: ActorLayer | null
  /** Q3 `mount`: what this actor rides, and the hop onto it. */
  mount: { on: string; since: number; dur: number; from: [number, number] } | null
  /** Q3 `dismount`: the hop off `from`'s top socket down to `pos`. Cleared once it has landed. */
  dismount: { from: string; since: number; dur: number } | null
  /**
   * Presentation only (`liveliness.ts`): the walk this actor is on, or last finished — scene second
   * it started, its length, the fraction covered now and its travel (stage %). Bob, lean and the
   * arrival settle are drawn from it; nothing physical reads it.
   */
  stride?: ActorStride | null
  /** Presentation only: the line this actor speaks now — word onsets drive the talking bounce. */
  voice?: ActorVoice | null
  /** Free moves in flight (`motion.ts`): drawn over the body, never moving its feet. */
  moves?: MotionState[]
  /** The pose kept from finished free moves (it grew, it fell over); absent = its own. */
  pose?: MotionPose
}

export interface ActorStride {
  /** The move cue's id. */
  id: string
  since: number
  dur: number
  /** Fraction of the path covered at the snapshot's `t` (`travelU`, eased). */
  k: number
  /** Where it set off (stage %) and the travel to its end: stage-width % across, stage-height % down. */
  from: [number, number]
  dx: number
  dy: number
  /** False when it chains on from / into another leg (no set-off / no stop). */
  easeIn: boolean
  easeOut: boolean
  clip: string
  exit: boolean
  /** The feet are off the ground for the trip (`walk_to.gait: air`): no step bob, no lean, no dust. */
  air?: boolean
}

export interface ActorVoice {
  since: number
  dur: number
  emotion: string | null
  /** Word onsets/ends, seconds into the line (the recording's own timings when it has them). */
  words: readonly { s: number; e: number }[]
}

/** Q6: which clip overrides which mask, from `since` until `until` (null = until let go). */
export interface ActorLayer {
  clip: string
  mask: string
  since: number
  /** Scene second at which the layer's weight reaches 0 again; null while it is held on. */
  until: number | null
}

/** Something a prop can be at: an actor's (or prop's) socket, another prop, or a stage point (feet). */
export type Place = { actor: string; socket: SocketName } | { prop: string } | { at: [number, number] }

/**
 * A prop's time-dependent motion (Q3). Pure data: `place.ts` turns it into a point at `t`, with
 * the sockets of the posed puppets, so a seek lands on the same frame a play-through draws.
 */
export type PropMotion =
  /** Easing into `heldBy`'s socket after an attach / pick-up / give. */
  | { kind: 'blend'; from: Place; since: number; dur: number }
  /** Leaving a socket for the floor point `to` — gravity-eased when `fall`. */
  | { kind: 'drop'; from: Place; to: [number, number]; since: number; dur: number; fall: boolean }
  /** A parabola `from` → `to` (`apex` = arc height, see the schema). */
  | { kind: 'toss'; from: Place; to: Place; apex: number; since: number; dur: number }
  /** Pushed / kicked / rolled along `path` (feet points), turning `turns` times from `rot0`. */
  | { kind: 'path'; path: [number, number][]; since: number; dur: number; rot0: number; turns: number }

export interface PropState {
  handle: string
  /** Feet point when it rests (stage %), or where the current motion will leave it. */
  pos: [number, number]
  anchor: string | null
  visible: boolean
  alpha: number
  /** Actor handle carrying it, or `null` when it rests on the stage. */
  heldBy: string | null
  /** The socket of `heldBy` it sits in. */
  socket: SocketName
  motion: PropMotion | null
  /** Rest rotation, radians (a rolled ball stays turned). */
  rot: number
  /** Story action bound by a `play_clip` on the prop; `idle` = its entry's `ambient` loop. */
  clip: string
  clipStartedAt: number
  prevClip: string | null
  prevClipStartedAt: number
  /** Q7 scenery drift, stage-% per second (x wraps at the edges); null = stays put. */
  drift: number | null
  /** Height at the reference row, stage-height % (`height_cm` × 0.34) — what a roll turns by. */
  refHeight: number
  /** Presentation only: the scene second a toss landed it on the floor and its little rebound (stage %). */
  landed?: { at: number; h: number } | null
  /** Free moves in flight and the pose kept from finished ones (see `ActorState`). */
  moves?: MotionState[]
  pose?: MotionPose
}

/** A Q8 effect in flight: drawn by `particles.ts`, a pure function of `(this, t)`. */
export interface FxState {
  id: string
  kind: string
  at: { actor: string } | { prop: string } | { at: [number, number] }
  t0: number
  dur: number
  scale: number
  /** `words`: what is written in the air. */
  text?: string
}

export interface CameraState {
  /** Centre of the view, percent of stage. `[50, 50]` is the full stage. */
  target: [number, number]
  /** 1.0 = the whole stage is visible. */
  zoom: number
  /**
   * Reduced motion's framing (SKEL-STORY §28, `camera.ts#cameraCut`): each camera cue sets it at
   * its `t`, so reduced motion cuts where a move would start. Absent until the first camera cue.
   */
  cut?: CameraFrame
  /** The shot of the last camera cue that started (`schema/story.ts#CameraShot`). */
  shot?: CameraShot | null
}

/** The active spoken line, if any. `tLocal` lets subtitles pick the word without its own clock. */
export interface LineState {
  cue: LineCue
  id: string
  text: string
  speaker: string | null
  tLocal: number
  dur: number
}

/** Everything `world.ts` needs to draw one frame. A pure function of `(scene, t)`. */
export interface StageState {
  /** Scene-local story seconds this snapshot describes. */
  t: number
  actors: Record<string, ActorState>
  props: Record<string, PropState>
  camera: CameraState
  line: LineState | null
  music: { track: string | null; gain_db: number }
  /** Q8 effects alive at `t` (their particles may outlive the cue by `FX_TAIL_S`). */
  fx: FxState[]
}

/** What the reducer needs besides the scene: the cast/prop tables of the bundle. */
export interface StageContext {
  cast: Bundle['cast']
  props: Bundle['props']
}

const EPS = 1e-6
/** Stage aspect: 1 % of width is 1.7778× as many pixels as 1 % of height. */
const ASPECT = 1920 / 1080

/** Q9(b): locomotion speeds up and slows down over this long at each end of a path. */
export const LOCO_RAMP_S = 0.25
/** Q9(e) / Q4: attach, give, pick-up and a plain detach ease positions over this long. */
export const ATTACH_BLEND_S = 0.15
/**
 * A thing taken up from where it lies eases into the hand a little longer for every stage % it
 * comes (`ATTACH_BLEND_PER` s), at most `ATTACH_BLEND_MAX_S`; the hand is taken to be
 * `ATTACH_LIFT` (stage-height %) above the feet.
 */
export const ATTACH_BLEND_PER = 0.018
export const ATTACH_BLEND_MAX_S = 0.55
export const ATTACH_LIFT = 10
/** How much higher than a hand a socket is (x `ATTACH_LIFT`): goggles go up onto the head. */
export const SOCKET_LIFT: Readonly<Record<string, number>> = { head: 2.6, top: 2.8, back: 1.6 }
/** From one socket of the same holder to another (hand -> head: goggles put on) — a lift, not a zip. */
export const ATTACH_RESOCKET_S = 0.4
/** A `detach` with `fall` drops over this long (gravity-eased) unless the cue says otherwise. */
export const FALL_S = 0.4
/** Q6: an upper-body layer blends in and out over this long. */
export const LAYER_BLEND_S = 0.2
/** Hop onto / off a mount. */
export const MOUNT_S = 0.3
/** Particles of an `fx` cue live at most this long after the cue's own `dur` (`particles.ts`). */
export const FX_TAIL_S = 2.5
/** The sky's weather (`particles.ts#maxLife`): snow and falling leaves take their time to cross
 * the stage after their emitter stops. */
export const WEATHER_TAIL_S = 9
const WEATHER_FX: ReadonlySet<string> = new Set(['snow', 'rain', 'leaves', 'petals', 'fireflies', 'twinkle'])

/** How long an effect's particles outlive its cue. */
export function fxTailS(kind: string): number {
  return WEATHER_FX.has(kind) ? WEATHER_TAIL_S : FX_TAIL_S
}
/** A scenery drift wraps this far (stage %) past each edge, so a cloud leaves before it returns. */
export const DRIFT_MARGIN = 15

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v)

/** Seconds a cue occupies. Zero-length cues (`face`, `sfx`, `music`, `checkpoint`) finish instantly. */
export function cueDur(cue: Cue): number {
  return 'dur' in cue && typeof cue.dur === 'number' && Number.isFinite(cue.dur) ? Math.max(0, cue.dur) : 0
}

export function isLineCue(cue: Cue): cue is LineCue {
  return cue.op === 'narrate' || cue.op === 'say' || cue.op === 'question'
}

/** Cues that own an actor's feet — a stage tap is swallowed while one runs (§8.6). */
export function isMoveCue(cue: Cue): boolean {
  return cue.op === 'walk_to' || cue.op === 'enter' || cue.op === 'exit'
}

export function lineText(cue: LineCue): string {
  return cue.op === 'question' ? cue.prompt : cue.text
}

export function lineSpeaker(cue: LineCue): string | null {
  return cue.op === 'say' ? cue.actor : null
}

/**
 * Word onsets of a `say` line, seconds into it: the recording's own timings, or — with no audio —
 * an even spread of the text's words over the line, so the talking bounce keeps a speech rhythm.
 */
export function spokenWords(cue: Extract<Cue, { op: 'say' }>): { s: number; e: number }[] {
  const words = cue.audio?.words
  if (words && words.length > 0) {
    return words.filter((w) => Number.isFinite(w.s) && Number.isFinite(w.e) && w.e >= w.s).map((w) => ({ s: w.s, e: w.e }))
  }
  const n = cue.text.split(/\s+/).filter(Boolean).length
  const dur = cueDur(cue)
  if (n === 0 || !(dur > EPS)) return []
  const step = dur / n
  return Array.from({ length: n }, (_, i) => ({ s: i * step, e: (i + 0.8) * step }))
}

/* --------------------------------------------------------- story timeline */

/**
 * The whole story as one line of seconds: every scene end to end, holds
 * excluded (a hold is a frozen clock, not story time). The grown-up seek bar
 * and the elapsed / total display read this; the runner itself still thinks
 * in scene-local seconds.
 */
export interface StoryTimeline {
  /** Story second at which each scene starts. `starts[0]` is always 0. */
  readonly starts: readonly number[]
  readonly durations: readonly number[]
  readonly total: number
}

export function storyTimeline(bundle: Pick<Bundle, 'scenes'>): StoryTimeline {
  const starts: number[] = []
  const durations: number[] = []
  let total = 0
  for (const scene of bundle.scenes) {
    const d = Number.isFinite(scene.duration) && scene.duration > 0 ? scene.duration : 0
    starts.push(total)
    durations.push(d)
    total += d
  }
  return Object.freeze({ starts: Object.freeze(starts), durations: Object.freeze(durations), total })
}

/**
 * Story seconds → `(scene, scene-local t)`, clamped to the story.
 *
 * A time exactly on a boundary belongs to the scene that *starts* there — a
 * seek to "the start of part 2" must not land in part 1's end-of-scene hold.
 * The one exception is the very end, which is the last scene at its duration
 * (its `scene_end` hold: "The end — again?").
 */
export function locateStoryTime(tl: StoryTimeline, storyT: number): { sceneIndex: number; t: number } {
  const n = tl.starts.length
  if (n === 0) return { sceneIndex: 0, t: 0 }
  const T = clamp(Number.isFinite(storyT) ? storyT : 0, 0, tl.total)
  for (let i = n - 1; i >= 0; i -= 1) {
    const start = tl.starts[i] as number
    if (T >= start - EPS && (tl.durations[i] as number) > 0) {
      const dur = tl.durations[i] as number
      return { sceneIndex: i, t: clamp(T - start, 0, dur) }
    }
  }
  return { sceneIndex: 0, t: 0 }
}

/* -------------------------------------------------------------- geometry */

/** Distance in "visual percent" — x is aspect-corrected so a diagonal path is paced correctly. */
function dist(a: readonly [number, number], b: readonly [number, number]): number {
  return Math.hypot((b[0] - a[0]) * ASPECT, b[1] - a[1])
}

/**
 * Point at fraction `u` of a polyline, by arc length. Pure; `u` is clamped.
 * An empty path returns `fallback`, a single-point path that point.
 */
export function pointOnPath(path: readonly (readonly [number, number])[], u: number, fallback: [number, number]): [number, number] {
  if (path.length === 0) return [fallback[0], fallback[1]]
  const first = path[0] as readonly [number, number]
  if (path.length === 1) return [first[0], first[1]]
  const uu = clamp(u, 0, 1)
  let total = 0
  const segs: number[] = []
  for (let i = 1; i < path.length; i += 1) {
    const d = dist(path[i - 1] as readonly [number, number], path[i] as readonly [number, number])
    segs.push(d)
    total += d
  }
  if (total <= EPS) {
    const last = path[path.length - 1] as readonly [number, number]
    return [last[0], last[1]]
  }
  let want = uu * total
  for (let i = 0; i < segs.length; i += 1) {
    const d = segs[i] as number
    if (want <= d || i === segs.length - 1) {
      const a = path[i] as readonly [number, number]
      const b = path[i + 1] as readonly [number, number]
      const f = d <= EPS ? 1 : clamp(want / d, 0, 1)
      return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f]
    }
    want -= d
  }
  const last = path[path.length - 1] as readonly [number, number]
  return [last[0], last[1]]
}

/** Facing implied by travel between two points; keeps `prev` when the move is vertical. */
function facingFromTravel(a: readonly [number, number], b: readonly [number, number], prev: 1 | -1): 1 | -1 {
  const dx = b[0] - a[0]
  if (Math.abs(dx) < 0.25) return prev
  return dx >= 0 ? 1 : -1
}

const smoothstep = (u: number): number => {
  const x = u <= 0 ? 0 : u >= 1 ? 1 : u
  return x * x * (3 - 2 * x)
}

/** ∫₀ˣ smoothstep — the distance covered while speeding up from rest (`x` in ramp units). */
const rampDist = (x: number): number => x * x * x - (x * x * x * x) / 2

/**
 * Q9(b): fraction of a path covered `tLocal` seconds into a `dur`-second move whose speed eases
 * in over `LOCO_RAMP_S` (smoothstep) at the start and out at the end — cruise in between. Either
 * end can be sharp (`easeIn/easeOut = false`) when the move chains onto another. Monotone, 0 → 1.
 */
export function travelU(tLocal: number, dur: number, easeIn = true, easeOut = true): number {
  if (!(dur > EPS)) return 1
  const t = clamp(tLocal, 0, dur)
  const ri = easeIn ? Math.min(LOCO_RAMP_S, dur / 2) : 0
  const ro = easeOut ? Math.min(LOCO_RAMP_S, dur / 2) : 0
  const total = dur - ri / 2 - ro / 2
  let d: number
  if (ri > 0 && t < ri) d = ri * rampDist(t / ri)
  else if (ro > 0 && t > dur - ro) d = total - ro * rampDist((dur - t) / ro)
  else d = ri / 2 + (t - ri)
  return clamp(d / total, 0, 1)
}

/** Inverse of `travelU`: the local second at which fraction `u` of the path is reached. */
export function travelT(u: number, dur: number, easeIn = true, easeOut = true): number {
  if (!(dur > EPS)) return 0
  let lo = 0
  let hi = dur
  for (let i = 0; i < 40; i += 1) {
    const mid = (lo + hi) / 2
    if (travelU(mid, dur, easeIn, easeOut) < u) lo = mid
    else hi = mid
  }
  return (lo + hi) / 2
}

/**
 * The on-screen facing at fraction `u` of `path` (each segment faces its own travel; a vertical
 * one keeps the facing before it) and the fraction at which it last changed (null = unchanged
 * from `prev`). Deterministic, so the turn tween starts at the same instant on every replay.
 */
export function pathFacing(path: readonly (readonly [number, number])[], u: number, prev: 1 | -1): { facing: 1 | -1; changedAtU: number | null } {
  if (path.length < 2) return { facing: prev, changedAtU: null }
  const segs: number[] = []
  let total = 0
  for (let i = 1; i < path.length; i += 1) {
    const d = dist(path[i - 1] as readonly [number, number], path[i] as readonly [number, number])
    segs.push(d)
    total += d
  }
  let f = prev
  let changedAtU: number | null = null
  let acc = 0
  for (let i = 0; i < segs.length; i += 1) {
    const u0 = total > EPS ? acc / total : 0
    if (u0 > u + EPS) break
    const next = facingFromTravel(path[i] as readonly [number, number], path[i + 1] as readonly [number, number], f)
    if (next !== f) {
      f = next
      changedAtU = u0
    }
    acc += segs[i] as number
  }
  return { facing: f, changedAtU }
}

/**
 * Q3 toss height: peak rise above the chord midpoint, stage-height percent. A toss into one's own
 * hands goes nowhere, so its rise is `apex` × 15: the bake sends up to ~3.7 for one that goes 1.5
 * body heights up (a toss capped at 1 rose 15 %, below a child's own head).
 */
export function tossRise(from: readonly [number, number], to: readonly [number, number], apex: number): number {
  return clamp(Number.isFinite(apex) ? apex : 0.35, 0, 4) * Math.max(dist(from, to), 15)
}

/**
 * Q3: the point of a toss at flight fraction `u` — a parabola from `from` to `to` whose middle rises
 * `tossRise` above the chord. Stage percent, y down.
 */
export function tossPoint(from: readonly [number, number], to: readonly [number, number], apex: number, u: number): [number, number] {
  const k = clamp(u, 0, 1)
  const h = tossRise(from, to, apex)
  return [from[0] + (to[0] - from[0]) * k, from[1] + (to[1] - from[1]) * k - 4 * h * k * (1 - k)]
}

/** Flight fraction `tLocal` into a toss: a third of a smoothstep over linear — soft release and landing. */
export function tossU(tLocal: number, dur: number): number {
  if (!(dur > EPS)) return 1
  const x = clamp(tLocal / dur, 0, 1)
  return x + (smoothstep(x) - x) * 0.3
}

/** Wrap a drifting x into `[-DRIFT_MARGIN, 100 + DRIFT_MARGIN)`. */
export function wrapDriftX(x: number): number {
  const span = 100 + 2 * DRIFT_MARGIN
  const v = (((x + DRIFT_MARGIN) % span) + span) % span
  return v - DRIFT_MARGIN
}

/* ----------------------------------------------------------------- clips */

/**
 * The one way an actor's clip changes, so the crossfade has a deterministic
 * history (`prevClip`, `prevClipStartedAt`) in every snapshot.
 *
 * A zero-length detour — `walk → idle → walk` at the same instant, which two
 * back-to-back `walk_to` cues produce — resumes the first clip at its own
 * start instead of restarting it: the walk cycle carries on with no pop.
 */
export function bindClip(a: { clip: string; clipStartedAt: number; prevClip: string | null; prevClipStartedAt: number }, clip: string, at: number, restart = false): void {
  if (restart) {
    // An explicit performance is a new occurrence, not continued locomotion.
    const blend = a.clip !== clip && at - a.clipStartedAt > EPS
    a.prevClip = blend ? a.clip : null
    a.prevClipStartedAt = blend ? a.clipStartedAt : 0
    a.clip = clip
    a.clipStartedAt = at
    return
  }
  if (a.clip === clip) return
  if (at - a.clipStartedAt > EPS) {
    a.prevClip = a.clip
    a.prevClipStartedAt = a.clipStartedAt
  } else if (a.prevClip === clip) {
    a.clip = clip
    a.clipStartedAt = a.prevClipStartedAt
    a.prevClip = null
    a.prevClipStartedAt = 0
    return
  }
  a.clip = clip
  a.clipStartedAt = at
}

function setFacing(a: ActorState, facing: 1 | -1, at: number): void {
  if (a.facing === facing) return
  a.facing = facing
  a.facingSince = at
}

/* --------------------------------------------------------- initial state */

function newActor(handle: string): ActorState {
  return {
    handle,
    pos: [50, 92],
    facing: 1,
    mirror: false,
    visible: false,
    alpha: 1,
    clip: 'idle',
    clipStartedAt: 0,
    prevClip: null,
    prevClipStartedAt: 0,
    emotion: null,
    holding: null,
    speaking: false,
    facingSince: Number.NEGATIVE_INFINITY,
    layer: null,
    mount: null,
    dismount: null,
  }
}

function newProp(handle: string, pos: [number, number], visible: boolean, drift: number | null, refHeight = 10): PropState {
  return {
    handle,
    pos,
    anchor: null,
    visible,
    alpha: 1,
    heldBy: null,
    socket: 'hand',
    motion: null,
    rot: 0,
    clip: 'idle',
    clipStartedAt: 0,
    prevClip: null,
    prevClipStartedAt: 0,
    drift,
    refHeight,
  }
}

/** Sockets that count as "holding" for `ActorState.holding` (a hat on the head does not). */
const HOLD_SOCKETS: ReadonlySet<string> = new Set(['hand', 'other_hand', 'mouth'])

/** Another prop `actor` still holds (a person holds two things, one per hand), or null. */
function otherHeld(s: StageState, actor: string, but: string, main = false): string | null {
  for (const q of Object.values(s.props)) {
    if (q.handle === but || q.heldBy !== actor || !HOLD_SOCKETS.has(q.socket)) continue
    if (!main || q.socket !== 'other_hand') return q.handle
  }
  return null
}

/** The scene at `t = 0`, before any cue: `initial` cast placement and `initial_props`. */
export function initialStageState(scene: BundleScene, ctx: StageContext): StageState {
  const actors: Record<string, ActorState> = {}
  for (const handle of Object.keys(ctx.cast)) actors[handle] = newActor(handle)
  for (const row of scene.initial) {
    const a = actors[row.handle] ?? newActor(row.handle)
    actors[row.handle] = {
      ...a,
      pos: [row.at[0], row.at[1]],
      facing: row.facing === 'left' ? -1 : 1,
      visible: row.visible,
      clip: row.clip || 'idle',
      clipStartedAt: 0,
      holding: (row as { holding?: string }).holding ?? null,
    }
  }

  const props: Record<string, PropState> = {}
  for (const [handle, entry] of Object.entries(ctx.props)) {
    // A Part 2 entry carries its own place (`at`) and is on stage unless the scene says otherwise;
    // a legacy one waits for `initial_props`.
    const at = entry?.at
    const drift = entry?.drift && Number.isFinite(entry.drift.dx_per_s) && entry.drift.dx_per_s !== 0 ? entry.drift.dx_per_s : null
    props[handle] = newProp(handle, at ? [at[0], at[1]] : [50, 92], at !== undefined, drift, referenceHeightPct(entry))
  }
  for (const row of scene.initial_props) {
    const p = props[row.handle] ?? newProp(row.handle, [50, 92], false, null)
    p.pos = [row.at[0], row.at[1]]
    p.anchor = row.anchor ?? null
    p.visible = row.visible
    const att = row.attached_to
    // a holder is someone, or a thing it stands on (the cake put on the table a chapter before)
    if (att && (actors[att.actor] || ctx.props[att.actor])) {
      p.heldBy = att.actor
      p.socket = att.socket
      if (actors[att.actor] && HOLD_SOCKETS.has(att.socket)) (actors[att.actor] as ActorState).holding = row.handle
    }
    props[row.handle] = p
  }
  // A cast member that starts holding a prop carries it from frame 0.
  for (const a of Object.values(actors)) {
    const p = a.holding ? props[a.holding] : undefined
    if (p && !p.heldBy) {
      p.heldBy = a.handle
      p.socket = 'hand'
    }
  }

  const s: StageState = {
    t: 0,
    actors,
    props,
    camera: { target: [50, 50], zoom: 1 },
    line: null,
    music: { track: scene.music ?? null, gain_db: 0 },
    fx: [],
  }
  resolveMirrors(s, ctx)
  return s
}

/**
 * The last step of every snapshot: turn each actor's on-screen `facing` into
 * a flip of the puppet view its action binds *now* (`puppet.ts#resolveAction`,
 * the same resolver the view draws with). Doing it here — after the replay,
 * from the final `(facing, clip)` pair — rather than inside the cues means a
 * `face`, a walk, an initial row and a clip change all obey one rule, and a
 * seek lands on exactly the flip a play-through shows.
 */
function resolveMirrors(s: StageState, ctx: StageContext): void {
  for (const a of Object.values(s.actors)) {
    const entry = ctx.cast[a.handle]
    const binding = entry?.render === 'sprite' ? resolveSpriteClip(entry.sprite, a.clip) : null
    a.mirror = binding && entry?.sprite
      ? mirrorFor(a.facing, { facing: spriteClipFacing(entry.sprite, binding), mirror: binding.clip.mirrored })
      : mirrorFor(a.facing, { facing: viewFacingFor(entry?.puppet, a.clip) })
  }
}

/* -------------------------------------------------------------- the apply */

function actorOf(s: StageState, handle: string): ActorState | null {
  return s.actors[handle] ?? null
}

/** A cue's `from`/`to`/`at`: an actor's socket, a prop, or a stage point. Unknown handles → null. */
function placeOf(s: StageState, target: string | readonly number[], socket: SocketName): Place | null {
  if (Array.isArray(target)) return { at: [Number(target[0]), Number(target[1])] }
  const h = target as string
  if (s.actors[h]) return { actor: h, socket }
  if (s.props[h]) return { prop: h }
  return null
}

/** Where a prop is now, as a `Place` to ease away from (its holder's socket, or its rest point). */
function currentPlace(p: PropState): Place {
  return p.heldBy ? { actor: p.heldBy, socket: p.socket } : { at: [p.pos[0], p.pos[1]] }
}

/** Fade a held-on layer out from `at` (its holder let go). */
function endLayer(a: ActorState | null, at: number): void {
  if (a?.layer && a.layer.until === null) a.layer.until = at + LAYER_BLEND_S
}

/**
 * `p` leaves its holder (if any) at scene second `at`. The `hold` layer is the main hand's pose:
 * it ends when that hand lets go, not when the other hand does (a juggler holding two things).
 */
function release(s: StageState, p: PropState, at: number): void {
  const holder = p.heldBy ? actorOf(s, p.heldBy) : null
  const main = holder !== null && HOLD_SOCKETS.has(p.socket) && p.socket !== 'other_hand'
  if (holder && (holder.holding === p.handle || main)) {
    if (main && otherHeld(s, holder.handle, p.handle, true) === null) endLayer(holder, at)
    if (holder.holding === p.handle) holder.holding = otherHeld(s, holder.handle, p.handle)
  }
  p.heldBy = null
}

/** `p` goes into `actor`'s `socket`, easing from where it was over `blend` seconds from `at`. */
function attachProp(s: StageState, p: PropState, actor: string, socket: SocketName, at: number, blend: number): void {
  const from = currentPlace(p)
  const same = p.heldBy === actor && p.socket === socket
  if (p.heldBy !== actor) release(s, p, at)
  const a = actorOf(s, actor)
  p.heldBy = actor
  p.socket = socket
  p.visible = true
  p.anchor = null
  p.motion = !same && blend > EPS ? { kind: 'blend', from, since: at, dur: blend } : null
  if (a && HOLD_SOCKETS.has(socket)) a.holding = p.handle
  else if (a && a.holding === p.handle) {
    // Hand → back (a satchel slung on): no longer in hand, so no longer "holding".
    a.holding = otherHeld(s, actor, p.handle)
    endLayer(a, at)
  }
}

/** Per-move-cue easing: a move that chains straight onto another of the same actor keeps its speed. */
export interface MoveLink {
  easeIn: boolean
  easeOut: boolean
}

const LINKS = new WeakMap<readonly Cue[], Map<string, MoveLink>>()

/** Which ends of each move cue ease (Q9b): none where a walk hands straight over to the next one. */
export function moveLinks(cues: readonly Cue[]): Map<string, MoveLink> {
  const cached = LINKS.get(cues)
  if (cached) return cached
  const out = new Map<string, MoveLink>()
  const moves = cues.filter(isMoveCue) as (Cue & { actor: string })[]
  for (const c of moves) {
    const end = c.t + cueDur(c)
    const before = moves.some((o) => o !== c && o.actor === c.actor && Math.abs(o.t + cueDur(o) - c.t) < 1e-3 && o.t < c.t + EPS)
    const after = moves.some((o) => o !== c && o.actor === c.actor && Math.abs(o.t - end) < 1e-3 && o.t > c.t - EPS)
    out.set(c.id, { easeIn: !before, easeOut: !after })
  }
  LINKS.set(cues, out)
  return out
}

/**
 * `apply(cue, tLocal)` of §8.1 — the single pure step. Mutates the snapshot
 * it is given (the snapshot is always a fresh replay, never a live object
 * shared with the renderer), and is called once per cue per replay with
 * `tLocal = clamp(t − cue.t, 0, dur)`.
 *
 * `finished` is `t ≥ cue.t + dur`: the cue's *effects* (§ADDENDUM 5.2 — the
 * prop actually changes hands, the actor ends at its destination) are
 * idempotent and applied on every replay in `t` order, which is exactly what
 * makes a seek land on the same world a play-through would.
 */
export function applyCue(s: StageState, cue: Cue, tLocal: number, finished: boolean, link: MoveLink = { easeIn: true, easeOut: true }, preserveBody = false): void {
  const dur = cueDur(cue)
  const u = dur <= EPS ? 1 : clamp(tLocal / dur, 0, 1)

  switch (cue.op) {
    case 'narrate':
    case 'question': {
      if (!finished) {
        s.line = { cue, id: cue.id, text: lineText(cue), speaker: null, tLocal, dur }
      } else if (s.line?.id === cue.id) {
        s.line = null
      }
      return
    }
    case 'say': {
      const a = actorOf(s, cue.actor)
      if (!finished) {
        s.line = { cue, id: cue.id, text: cue.text, speaker: cue.actor, tLocal, dur }
        if (a) {
          if (!preserveBody) bindClip(a, cue.clip || 'talk', cue.t)
          a.emotion = cue.emotion ?? a.emotion
          a.speaking = true
          a.visible = true
          a.voice = { since: cue.t, dur, emotion: lineFeeling(cue.emotion, cue.text), words: spokenWords(cue) }
        }
      } else {
        if (s.line?.id === cue.id) s.line = null
        if (a) {
          a.speaking = false
          if (a.voice?.since === cue.t) a.voice = null
          if (!preserveBody) bindClip(a, 'idle', cue.t + dur)
        }
      }
      return
    }
    case 'enter':
    case 'walk_to':
    case 'exit': {
      const a = actorOf(s, cue.actor)
      if (!a) return
      const fallback: [number, number] = cue.op === 'walk_to' || cue.op === 'enter' ? [cue.to[0], cue.to[1]] : a.pos
      const path = cue.path as readonly (readonly [number, number])[]
      // Q9(b): the feet speed up and slow down; the facing follows each segment's travel.
      const k = finished ? 1 : travelU(tLocal, dur, link.easeIn, link.easeOut)
      const pf = pathFacing(path, k, a.facing)
      if (pf.changedAtU !== null) setFacing(a, pf.facing, cue.t + travelT(pf.changedAtU, dur, link.easeIn, link.easeOut))
      a.pos = pointOnPath(path, k, fallback)
      a.visible = true
      bindClip(a, cue.clip || 'move', cue.t)
      const first = path[0] ?? fallback
      const last = path[path.length - 1] ?? fallback
      a.stride = {
        id: cue.id, since: cue.t, dur, k, from: [first[0], first[1]], dx: last[0] - first[0], dy: last[1] - first[1],
        easeIn: link.easeIn, easeOut: link.easeOut, clip: cue.clip || 'move', exit: cue.op === 'exit',
        ...(cue.op === 'walk_to' && cue.gait === 'air' ? { air: true } : {}),
      }
      // The walk → idle crossfade starts as the feet start to slow, so legs and body settle together.
      const settle = link.easeOut ? Math.min(LOCO_RAMP_S, dur / 2) : 0
      if (link.easeOut && dur > EPS && tLocal >= dur - settle - EPS) bindClip(a, 'idle', cue.t + dur - settle)
      if (finished) {
        bindClip(a, 'idle', cue.t + dur - settle)
        if (cue.op === 'exit') a.visible = false
      }
      return
    }
    case 'face': {
      const a = actorOf(s, cue.actor)
      if (!a) return
      if (typeof cue.dir === 'string') {
        setFacing(a, cue.dir === 'left' ? -1 : 1, cue.t)
      } else {
        const other = actorOf(s, cue.dir.toward) ?? s.props[cue.dir.toward] ?? null
        if (other && Math.abs(other.pos[0] - a.pos[0]) > EPS) setFacing(a, other.pos[0] >= a.pos[0] ? 1 : -1, cue.t)
      }
      return
    }
    case 'play_clip': {
      const a = actorOf(s, cue.actor)
      if (!a) {
        // A prop plays its own actions too (`roll`, `open`, `spin`…); layers are for actors.
        const p = s.props[cue.actor]
        if (!p || cue.layer) return
        bindClip(p, cue.clip, cue.t, true)
        if (finished) bindClip(p, cue.then || 'idle', cue.t + dur)
        return
      }
      if (cue.layer === 'upper') {
        if (!cue.clip || cue.clip === 'none') {
          if (a.layer) a.layer.until = Math.min(a.layer.until ?? Number.POSITIVE_INFINITY, cue.t + LAYER_BLEND_S)
          return
        }
        a.layer = { clip: cue.clip, mask: cue.mask || 'upper', since: cue.t, until: cue.then === cue.clip ? null : cue.t + dur }
        return
      }
      bindClip(a, cue.clip, cue.t, true)
      if (finished) bindClip(a, cue.then || 'idle', cue.t + dur)
      return
    }
    case 'emote': {
      const a = actorOf(s, cue.actor)
      if (!a) return
      a.emotion = finished ? null : cue.emotion
      return
    }
    case 'pick_up': {
      if (!finished) return
      const p = s.props[cue.prop]
      if (p) attachProp(s, p, cue.actor, 'hand', cue.t + dur, ATTACH_BLEND_S)
      else if (actorOf(s, cue.actor)) (actorOf(s, cue.actor) as ActorState).holding = cue.prop
      return
    }
    case 'put_down': {
      if (!finished) return
      const a = actorOf(s, cue.actor)
      const p = s.props[cue.prop]
      if (a && a.holding === cue.prop) a.holding = null
      if (p) {
        const from = currentPlace(p)
        release(s, p, cue.t + dur)
        p.pos = [cue.at[0], cue.at[1]]
        p.anchor = cue.anchor ?? null
        p.visible = true
        p.motion = { kind: 'drop', from, to: [cue.at[0], cue.at[1]], since: cue.t + dur, dur: ATTACH_BLEND_S, fall: false }
      }
      return
    }
    case 'give': {
      if (!finished) return
      const from = actorOf(s, cue.from)
      const to = actorOf(s, cue.to)
      const p = s.props[cue.prop]
      if (from && from.holding === cue.prop) from.holding = null
      endLayer(from, cue.t + dur)
      if (p) attachProp(s, p, cue.to, 'hand', cue.t + dur, ATTACH_BLEND_S)
      else if (to) to.holding = cue.prop
      return
    }
    case 'attach': {
      const p = s.props[cue.prop]
      const holder = actorOf(s, cue.actor)
      if (!p || !holder) return
      let blend = typeof cue.blend_ms === 'number' && Number.isFinite(cue.blend_ms) ? cue.blend_ms / 1000 : ATTACH_BLEND_S
      // Up off the floor (or wherever it lay) into the hand, or over from another's hand: the
      // further it comes, the longer it takes — a lift, a hand-over, not a zip. The live sandcastle
      // shot 14 % up into a hand that has no pick-up of its own in 0.15 s; a seed flew 15 % from a
      // little bird to a big fox.
      const giver = p.heldBy ? actorOf(s, p.heldBy) : null
      const lift = ATTACH_LIFT * (SOCKET_LIFT[cue.socket] ?? 1)
      const d = giver
        ? Math.hypot((giver.pos[0] - holder.pos[0]) * ASPECT, giver.pos[1] - holder.pos[1])
        : Math.hypot((p.pos[0] - holder.pos[0]) * ASPECT, p.pos[1] - holder.pos[1] + lift)
      if (p.heldBy !== cue.actor) blend = Math.max(blend, Math.min(ATTACH_BLEND_MAX_S, ATTACH_BLEND_S + d * ATTACH_BLEND_PER))
      else if (p.socket !== cue.socket) blend = Math.max(blend, ATTACH_RESOCKET_S)
      attachProp(s, p, cue.actor, cue.socket, cue.t, blend)
      return
    }
    case 'detach': {
      const p = s.props[cue.prop]
      if (!p) return
      const from = currentPlace(p)
      const holder = p.heldBy ? actorOf(s, p.heldBy) : null
      release(s, p, cue.t)
      const to: [number, number] = [cue.at[0], cue.at[1]]
      let fallDur = dur > EPS ? dur : cue.fall ? FALL_S : ATTACH_BLEND_S
      if (dur <= EPS && !cue.fall && holder) {
        // set down out of the hand: as long as the way down takes (a put-down, not a zip)
        const d = Math.hypot((to[0] - holder.pos[0]) * ASPECT, to[1] - holder.pos[1] + ATTACH_LIFT)
        fallDur = Math.max(fallDur, Math.min(ATTACH_BLEND_MAX_S, ATTACH_BLEND_S + d * ATTACH_BLEND_PER))
      }
      p.pos = to
      // Visibility is left alone: an eaten apple is `hide`n in the hand and only then let go of
      // (so the holder's `hold` layer ends) — it must not reappear at the holder's feet.
      p.anchor = null
      p.motion = { kind: 'drop', from, to, since: cue.t, dur: fallDur, fall: cue.fall }
      return
    }
    case 'toss': {
      const p = s.props[cue.prop]
      if (!p) return
      // Tossed up from where it lies (`from` = the thing itself, the live juggle's second and third
      // apples): from its own place — as a place of its own it would resolve through this very toss
      // and start the flight in the middle of the stage.
      const from =
        cue.from === cue.prop
          ? currentPlace(p)
          : (placeOf(s, cue.from, p.heldBy && p.heldBy === cue.from ? p.socket : 'hand') ?? currentPlace(p))
      const catcher = typeof cue.to === 'string' ? actorOf(s, cue.to) : null
      const catches = cue.then === 'catch' && catcher !== null
      // Landing on an actor means at its feet; catching means into its hand (`socket`: a juggler's
      // other hand, or up onto its head).
      const into: SocketName = cue.socket ?? 'hand'
      const to: Place =
        catches && catcher ? { actor: catcher.handle, socket: into }
        : catcher ? { at: [catcher.pos[0], catcher.pos[1]] }
        : placeOf(s, cue.to, 'hand') ?? { at: [p.pos[0], p.pos[1]] }
      release(s, p, cue.t)
      p.visible = true
      p.anchor = null
      if (!finished) {
        p.motion = { kind: 'toss', from, to, apex: cue.apex, since: cue.t, dur }
        return
      }
      p.motion = null
      if (catches && catcher) {
        attachProp(s, p, catcher.handle, into, cue.t + dur, 0)
        return
      }
      const land: [number, number] = 'at' in to ? to.at : 'prop' in to ? [...(s.props[to.prop]?.pos ?? p.pos)] : p.pos
      p.pos = [land[0], land[1]]
      // A landing rebounds once, higher for a higher arc (1-3 % of the stage): drawn by place.ts.
      if (dur > EPS) p.landed = { at: cue.t + dur, h: 3 * clamp(Number.isFinite(cue.apex) ? cue.apex : 0.35, 0.33, 1) }
      if (cue.then === 'vanish') p.visible = false
      return
    }
    case 'prop_move': {
      const p = s.props[cue.prop]
      if (!p) return
      release(s, p, cue.t)
      p.visible = true
      p.anchor = null
      const path = (cue.path.length > 0 ? cue.path : [p.pos]).map((q) => [q[0], q[1]] as [number, number])
      const turns = spinTurns(cue.spin, path, p.refHeight)
      if (!finished) {
        p.motion = { kind: 'path', path, since: cue.t, dur, rot0: p.rot, turns }
        return
      }
      p.motion = null
      const last = path[path.length - 1] as [number, number]
      p.pos = [last[0], last[1]]
      p.rot += turns * 2 * Math.PI
      return
    }
    case 'fx': {
      const at = placeOf(s, cue.at, 'hand')
      if (!at) return
      const anchor: FxState['at'] = 'actor' in at ? { actor: at.actor } : at
      s.fx.push({ id: cue.id, kind: cue.kind, at: anchor, t0: cue.t, dur: Math.max(0, dur), scale: cue.scale ?? 1, ...(cue.text ? { text: cue.text } : {}) })
      return
    }
    case 'mount': {
      const a = actorOf(s, cue.actor)
      if (!a || cue.on === cue.actor || !(s.actors[cue.on] || s.props[cue.on])) return
      a.mount = { on: cue.on, since: cue.t, dur: dur > EPS ? dur : MOUNT_S, from: [a.pos[0], a.pos[1]] }
      a.dismount = null
      a.visible = true
      bindClip(a, 'ride', cue.t)
      return
    }
    case 'dismount': {
      const a = actorOf(s, cue.actor)
      if (!a) return
      if (a.mount) a.dismount = { from: a.mount.on, since: cue.t, dur: dur > EPS ? dur : MOUNT_S }
      a.mount = null
      a.pos = [cue.at[0], cue.at[1]]
      bindClip(a, 'idle', cue.t)
      return
    }
    case 'hide': {
      const a: { visible: boolean; alpha: number } | null = actorOf(s, cue.actor) ?? s.props[cue.actor] ?? null
      if (!a) return
      if (finished) {
        a.visible = false
        a.alpha = 1
      } else {
        a.alpha = cue.how === 'fade' ? 1 - u : 1
        a.visible = cue.how === 'pop' ? u < 1 : true
      }
      return
    }
    case 'appear': {
      const a = actorOf(s, cue.actor)
      if (!a) {
        const p = s.props[cue.actor]
        if (!p) return
        release(s, p, cue.t)
        p.motion = null
        p.pos = [cue.at[0], cue.at[1]]
        p.anchor = cue.anchor ?? null
        p.visible = true
        p.alpha = cue.how === 'fade' && !finished ? u : 1
        return
      }
      a.pos = [cue.at[0], cue.at[1]]
      a.visible = true
      a.alpha = cue.how === 'fade' && !finished ? u : 1
      if (cue.clip) bindClip(a, cue.clip, cue.t)
      return
    }
    case 'camera': {
      const to: [number, number] = Array.isArray(cue.target)
        ? [cue.target[0], cue.target[1]]
        : (() => {
            const a = actorOf(s, cue.target.actor)
            return a ? ([a.pos[0], Math.max(0, a.pos[1] - 18)] as [number, number]) : s.camera.target
          })()
      const from = s.camera
      const k = cue.ease === 'linear' ? u : smoothstep(u)
      // §28: the centre and the view width (1/zoom) lerp, so a still subject inside both ends of
      // the move stays inside every frame between them.
      const goal = { target: to, zoom: cue.zoom }
      const shot = cue.shot ?? null
      const cut = cameraCut(shot, goal) ?? from.cut
      s.camera = { ...interpCamera(from, goal, k), shot, ...(cut ? { cut } : {}) }
      return
    }
    case 'music': {
      s.music = { track: cue.track, gain_db: cue.gain_db }
      return
    }
    case 'motion': {
      // A free move (`motion.ts`): in flight it rides over the body; once done, a kept move's last
      // pose stays on it (and a reset one gives the body back its own).
      const body: { moves?: MotionState[]; pose?: MotionPose } | null = actorOf(s, cue.actor) ?? s.props[cue.actor] ?? null
      if (!body) return
      const m: MotionState = {
        id: cue.id,
        t0: cue.t,
        dur,
        keys: cue.keys,
        center: cue.pivot === 'center',
        keep: cue.keep === true,
        reset: cue.reset === true,
        shake: cue.shake ?? null,
        from: body.pose ?? { dx: 0, dy: 0, rot: 0, sx: 1, sy: 1, a: 1 },
      }
      if (!finished) {
        body.moves = [...(body.moves ?? []).filter((o) => o.id !== m.id), m]
        return
      }
      body.moves = (body.moves ?? []).filter((o) => o.id !== m.id)
      if (m.keep || m.reset) body.pose = finalPose(m)
      return
    }
    case 'wait':
    case 'sfx':
    case 'checkpoint':
      // `wait` is pure schedule; `sfx` is an effect fired by the scheduler,
      // never replayed on a seek (ADDENDUM §5.2); `checkpoint` freezes the
      // clock and carries no world state.
      return
    default:
      return
  }
}

/**
 * Turns a `prop_move` makes: a number is taken as is; `true` rolls — the distance travelled over
 * the circumference, signed by the direction of travel.
 */
export function spinTurns(spin: boolean | number | undefined, path: readonly (readonly [number, number])[], diameterPct: number): number {
  if (typeof spin === 'number') return Number.isFinite(spin) ? spin : 0
  if (spin !== true || path.length < 2) return 0
  let d = 0
  for (let i = 1; i < path.length; i += 1) d += dist(path[i - 1] as readonly [number, number], path[i] as readonly [number, number])
  const first = path[0] as readonly [number, number]
  const last = path[path.length - 1] as readonly [number, number]
  const dir = last[0] >= first[0] ? 1 : -1
  const c = Math.PI * Math.max(diameterPct, 1)
  return (dir * d) / c
}

/** The props' `height_cm` → stage-height percent at the reference row (34 % per 100 cm, §8.2). */
function referenceHeightPct(entry: StageContext['props'][string] | undefined): number {
  const cm = entry?.height_cm ?? entry?.size_cm ?? 30
  return cm * 0.34
}

/**
 * The world riders see: a mounted actor stands where its mount stands (the renderer lifts it onto
 * the `top` socket) and faces the way it faces; landed hops, finished blends and faded layers and
 * effects are dropped so the snapshot only carries what still moves.
 */
function settle(s: StageState): void {
  const t = s.t
  for (const a of Object.values(s.actors)) {
    if (a.mount) {
      const m = s.actors[a.mount.on]
      const p = s.props[a.mount.on]
      if (m) {
        a.pos = [m.pos[0], m.pos[1]]
        if (m.facing !== a.facing) {
          a.facing = m.facing
          a.facingSince = m.facingSince
        }
      } else if (p) {
        a.pos = [p.pos[0], p.pos[1]]
      }
    }
    if (a.dismount && t >= a.dismount.since + a.dismount.dur - EPS) a.dismount = null
    if (a.layer && a.layer.until !== null && t >= a.layer.until - EPS) a.layer = null
  }
  for (const p of Object.values(s.props)) {
    const m = p.motion
    if (m && (m.kind === 'blend' || m.kind === 'drop') && t >= m.since + m.dur - EPS) p.motion = null
  }
  s.fx = s.fx.filter((f) => t < f.t0 + f.dur + fxTailS(f.kind))
}

/**
 * The whole determinism contract in six lines: replay every cue that has
 * started, at its clamped local time, from the scene's initial state.
 * `snapshot(t)` therefore equals `seek(t)` by construction.
 */
export function stageStateAt(scene: BundleScene, ctx: StageContext, t: number, cues: readonly Cue[] = cuesOf(scene)): StageState {
  const s = initialStageState(scene, ctx)
  s.t = t
  const links = moveLinks(cues)
  for (const cue of cues) {
    if (cue.t > t + EPS) break
    const dur = cueDur(cue)
    const tLocal = clamp(t - cue.t, 0, dur)
    applyCue(s, cue, tLocal, t >= cue.t + dur - EPS, links.get(cue.id), !!scene.event_timing)
    if (scene.event_timing && t < cue.t + dur - EPS &&
        (cue.op === 'walk_to' || cue.op === 'enter' || cue.op === 'exit' || cue.op === 'play_clip')) {
      const actor = s.actors[cue.actor]
      if (actor) actor.essentialMotion = true
    }
  }
  if (scene.event_timing && s.line?.cue.op === 'say') {
    const actor = s.actors[s.line.cue.actor]
    if (actor && !actor.essentialMotion && actor.clip === 'idle') {
      bindClip(actor, s.line.cue.clip || 'talk', Math.max(s.line.cue.t, actor.clipStartedAt))
    }
  }
  settle(s)
  resolveMirrors(s, ctx)
  return s
}

/* ----------------------------------------------------------- the runner */

export interface HoldPolicy {
  /** Seconds after which the hold releases itself, or `null` for "wait for a tap". */
  autoAfter: number | null
  /** Seconds at which a spoken nudge plays (tap-gates in tap mode: 6 and 12). */
  nudges: readonly number[]
  /** Seconds at which the hold fires anyway — a gate stalls mid-action, a scene end does not. */
  forceAfter: number | null
}

export interface RunnerPrefs {
  lowDistraction?: boolean
  mode: PlayerMode
  /** §8.6: `question` cues *and* their checkpoints are dropped from the runnable list when off. */
  questions: boolean
  /** Grown-up panel: auto-answer a question hold after `options.question_auto_s`. */
  autoAnswer: boolean
  /** Seconds at a hold before the player dims and releases the wake lock (§8.6). */
  idleDimS: number
  /** ADDENDUM §5.3 tap-gate timings. */
  tapGateAutoS: number
  tapGateNudgeS: readonly number[]
  tapGateForceS: number
}

export interface RunnerEvents {
  /** Anything that could change what the HUD shows. */
  state: () => void
  /** A line cue started; the audio track plays it (or Web Speech does — BUILD-OVERRIDES). */
  line: (cue: LineCue) => void
  lineEnd: (cue: LineCue) => void
  sfx: (cue: Extract<Cue, { op: 'sfx' }>) => void
  music: (cue: Extract<Cue, { op: 'music' }>) => void
  /** A hold began. `verbCue` is set for ADDENDUM §5.3 tap gates. */
  hold: (kind: HoldKind, cue: CheckpointCue) => void
  holdRelease: (kind: HoldKind, how: 'tap' | 'auto' | 'forced' | 'queued') => void
  /** Tap-gate nudge at 6 s / 12 s (§5.3). The audio track speaks it. */
  nudge: (index: number, kind: HoldKind) => void
  dim: (on: boolean) => void
  sceneChange: (index: number) => void
  ended: () => void
  seek: (t: number) => void
}

type Listener<K extends keyof RunnerEvents> = RunnerEvents[K]
/** Listeners are stored erased: a union of handler types is contravariant and will not unify. */
type AnyListener = (...args: never[]) => void

export interface RunnerOptions {
  bundle: Bundle
  audio?: AudioPort | undefined
  prefs?: Partial<RunnerPrefs> | undefined
  /** Wall clock for the debounce only; injected so the policy table is testable. */
  now?: (() => number) | undefined
  /**
   * Asked before switching to a scene; the loader preloads it and the renderer builds it. Resolves
   * when it may start. `entry` says how it is entered (a play-through snapshots the old scene).
   */
  loadScene?: ((index: number, opts: { entry: SceneEntry }) => Promise<void>) | undefined
  clock?: StoryClock | undefined
}

/** §8.6: a stage tap inside the last 30 % of a line is queued as one *continue*. */
export const TAP_QUEUE_FRACTION = 0.7
/** §8.6: every accepted tap starts a 300 ms debounce. */
export const TAP_DEBOUNCE_MS = 300

export function defaultPrefs(bundle: Bundle): RunnerPrefs {
  const o = bundle.options as Bundle['options'] & {
    tap_gate_auto_s?: number
    tap_gate_nudge_s?: number[]
    tap_gate_force_s?: number
  }
  return {
    mode: 'tap',
    questions: bundle.options.questions_default,
    autoAnswer: false,
    idleDimS: bundle.options.idle_dim_s,
    tapGateAutoS: o.tap_gate_auto_s ?? 8,
    tapGateNudgeS: o.tap_gate_nudge_s ?? [6, 12],
    tapGateForceS: o.tap_gate_force_s ?? 18,
  }
}

export class TimelineRunner {
  readonly bundle: Bundle
  readonly clock: StoryClock

  private prefs: RunnerPrefs
  private audio: AudioPort | null
  private readonly nowMs: () => number
  private readonly loadScene: ((index: number, opts: { entry: SceneEntry }) => Promise<void>) | null

  private listeners = new Map<keyof RunnerEvents, Set<AnyListener>>()

  private _sceneIndex = 0
  private _phase: Phase = 'paused'
  private _dimmed = false

  /** The runnable cue list for the current scene (questions dropped when off). */
  private cues: Cue[] = []
  /** Cue ids whose *effects* (audio, sfx, music) have already fired on the way forward. */
  private fired = new Set<string>()
  /** Checkpoint ids already released. A backwards seek un-consumes the ones after it. */
  private passed = new Set<string>()

  private holdCue: CheckpointCue | null = null
  private holdElapsedMs = 0
  private holdNudged = 0
  private continueQueued = false
  private lastTapMs = -Infinity
  private activeLine: LineCue | null = null
  /** True while the active line's voice is actually playing (not merely armed). */
  private lineAudible = false
  /** A grown-up is dragging the seek bar: the story is frozen and seeks are silent. */
  private scrubbing = false
  private destroyed = false

  /** How the current scene was entered: its transition plays only for `play` and `start`. */
  private _entry: SceneEntry = 'start'
  /** A play-through is loading the next scene: a tick past the end must not ask again. */
  private advancing = false
  /** Scene loads in flight, and a pause pressed during one (it must survive the load). */
  private loadingScenes = 0
  private pauseRequested = false
  /** Scene second at which the story's `ending` finishes, while it plays; null otherwise. */
  private outroEnd: number | null = null
  /** More parts are coming (a story still being made): the last ready part's end is a wait. */
  private streamOpen = false

  constructor(opts: RunnerOptions) {
    this.bundle = opts.bundle
    this.clock = opts.clock ?? new StoryClock()
    this.prefs = { ...defaultPrefs(opts.bundle), ...(opts.prefs ?? {}) }
    this.audio = opts.audio ?? null
    this.nowMs = opts.now ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()))
    this.loadScene = opts.loadScene ?? null
    this.rebuildCues()
    // Scene 0's opening is covered from the first frame, behind "Tap to start" too.
    this.prerollOpening()
  }

  /* -------------------------------------------------------- tiny emitter */

  on<K extends keyof RunnerEvents>(ev: K, cb: Listener<K>): () => void {
    let set = this.listeners.get(ev)
    if (!set) {
      set = new Set<AnyListener>()
      this.listeners.set(ev, set)
    }
    const fn = cb as AnyListener
    set.add(fn)
    return () => {
      set?.delete(fn)
    }
  }

  private emit<K extends keyof RunnerEvents>(ev: K, ...args: Parameters<Listener<K>>): void {
    const set = this.listeners.get(ev)
    if (!set) return
    for (const cb of [...set]) (cb as unknown as (...a: Parameters<Listener<K>>) => void)(...args)
  }

  /* ------------------------------------------------------------ getters */

  get sceneIndex(): number {
    return this._sceneIndex
  }

  get sceneCount(): number {
    return this.bundle.scenes.length
  }

  get scene(): BundleScene {
    const s = this.bundle.scenes[this._sceneIndex]
    if (!s) throw new Error(`player: scene ${this._sceneIndex} is out of range`)
    return s
  }

  get phase(): Phase {
    return this._phase
  }

  get dimmed(): boolean {
    return this._dimmed
  }

  /** Scene-local story seconds. Below 0 during a transition's pre-roll, past `duration` in the ending. */
  get t(): number {
    return this.clock.t
  }

  /** The raw scene clock the renderer times a transition by (the same number as `t`). */
  get sceneT(): number {
    return this.clock.t
  }

  /** How the current scene was entered (`transition.ts#SceneEntry`). */
  get entry(): SceneEntry {
    return this._entry
  }

  /** The story's ending is playing (the last hold was released; `ended` follows). */
  get inOutro(): boolean {
    return this.outroEnd !== null
  }

  /** At the end of the ready parts, waiting for the next one to be made. */
  get isWaiting(): boolean {
    return this._phase === 'waiting'
  }

  /** A scene is being loaded (a "Next", a jump): the one on screen is about to be torn down. */
  get loadingScene(): boolean {
    return this.loadingScenes > 0
  }

  get holdKind(): HoldKind | null {
    return this.holdCue ? this.holdCue.kind : null
  }

  get holdLabel(): string | null {
    return this.holdCue ? this.holdCue.label : null
  }

  /** ADDENDUM §5.3: the verb cue a `tap_gate` hold is gating, for the anticipation loop. */
  get gatedCue(): Cue | null {
    const id = this.holdCue?.verb_cue
    if (!id) return null
    return this.cues.find((c) => c.id === id) ?? null
  }

  get runnableCues(): readonly Cue[] {
    return this.cues
  }

  /** True when there is an earlier line to replay — the ↺ button's enabled state. */
  get canReplayLine(): boolean {
    return this.lastLineCue() !== null
  }

  get line(): LineCue | null {
    return this.activeLine
  }

  get isScrubbing(): boolean {
    return this.scrubbing
  }

  /**
   * The grown-up seek bar is being dragged (`controls.ts`). While it is, the
   * story is frozen — no ticks, no hold timers — and every `seek()` re-arms
   * the line it lands in *silently*, so a drag across three sentences does not
   * stutter out thirty half-words. Letting go restarts the voice of whatever
   * line the drag ended in, at its offset.
   */
  setScrubbing(on: boolean): void {
    if (this.destroyed || on === this.scrubbing) return
    this.scrubbing = on
    if (on) {
      this.silenceLine()
      this.audio?.pauseBackground?.(true)
      this.audio?.stopSounds?.()
    } else {
      this.resumeLineAudio()
      this.syncMusic()
    }
    this.emit('state')
  }

  getPrefs(): RunnerPrefs {
    return { ...this.prefs }
  }

  setAudio(audio: AudioPort | null): void {
    this.audio = audio
  }

  /**
   * Change grown-up-panel preferences. Toggling `questions` rebuilds the
   * runnable list from the untouched bundle, so the switch is reversible
   * (§4.6.3 keeps the cues in the script for exactly this reason).
   */
  setPrefs(p: Partial<RunnerPrefs>): void {
    const questionsChanged = p.questions !== undefined && p.questions !== this.prefs.questions
    this.prefs = { ...this.prefs, ...p }
    if (p.lowDistraction !== undefined) {
      this.audio?.stopSounds?.()
      this.syncMusic()
    }
    if (questionsChanged) {
      this.rebuildCues()
      // a pre-roll or the ending has no cue to re-arm: let it play on. This re-arm is not the
      // grown-up seeking, so it is not a cut either: a transition still uncovering plays on.
      // Waiting at the end of the ready parts has nothing to re-arm either.
      if (this.outroEnd === null && this.clock.t >= 0 && this._phase !== 'waiting') {
        const entry = this._entry
        this.seek(this.clock.t)
        this._entry = entry
      }
    }
    this.emit('state')
  }

  private rebuildCues(): void {
    const all = cuesOf(this.scene)
    if (this.prefs.questions) {
      this.cues = [...all]
      return
    }
    // Drop `question` cues AND the `question` checkpoints that follow them.
    this.cues = all.filter((c) => !(c.op === 'question' || (c.op === 'checkpoint' && c.kind === 'question')))
  }

  /* ------------------------------------------------------------ snapshot */

  /**
   * The pure world at scene-local time `t`. `snapshot()` with no argument = now. Clamped to the
   * scene: a pre-roll draws the scene's first frame, the ending its last.
   */
  snapshot(t: number = this.clock.t): StageState {
    const at = clamp(Number.isFinite(t) ? t : 0, 0, this.scene.duration)
    return stageStateAt(this.scene, { cast: this.bundle.cast, props: this.bundle.props }, at, this.cues)
  }

  /* ------------------------------------------------------------ playback */

  private syncMusic(): void {
    if (this._phase !== 'playing' || this.scrubbing) return
    let track = this.scene.music ?? null
    let gain = -20
    for (const cue of this.cues) {
      if (cue.t > Math.max(0, this.clock.t)) break
      if (cue.op === 'music') { track = cue.track; gain = cue.gain_db }
    }
    this.audio?.music(track, gain - (this.prefs.lowDistraction ? 8 : 0), 1.5)
    this.audio?.pauseBackground?.(false)
  }

  play(): void {
    if (this.destroyed) return
    this.pauseRequested = false
    // Nothing to play until the next part arrives; a pause pressed during the wait is taken back.
    if (this._phase === 'waiting') {
      this.emit('state')
      return
    }
    if (this._phase === 'ended') {
      // "Again": the story's opening plays once more
      void this.gotoScene(0, { autoplay: true, entry: 'start' })
      return
    }
    if (this._phase === 'paused') {
      this._phase = this.holdCue ? this.holdPhase(this.holdCue.kind) : 'playing'
      if (!this.holdCue) this.clock.resume()
      // `pause()` silenced the line but left it armed; without this the rest of
      // the sentence is simply gone and the subtitle runs on in silence.
      this.resumeLineAudio()
      this.syncMusic()
      this.emit('state')
      return
    }
    if (this._phase === 'playing') return
    this.emit('state')
  }

  pause(): void {
    if (this._phase === 'ended' || this._phase === 'paused') return
    // Pressed while waiting for the next part: the phase stays (the clock is already stopped),
    // and the part lands paused when it arrives instead of playing over the pause.
    if (this._phase === 'waiting') {
      this.pauseRequested = true
      this.emit('state')
      return
    }
    // Pressed while the next scene loads: the load must not autoplay over it.
    if (this.loadingScenes > 0) this.pauseRequested = true
    this.clock.pause()
    this.audio?.pauseBackground?.(true)
    this.audio?.stopSounds?.()
    this.silenceLine()
    this._phase = 'paused'
    this.emit('state')
  }

  /** Start (or restart) the story. Used after the unlock gesture. */
  start(): void {
    this.prerollOpening()
    this.clock.resume()
    this._phase = 'playing'
    this.syncMusic()
    this.resumeLineAudio()
    this.emit('state')
  }

  private holdPhase(kind: HoldKind): Phase {
    return kind === 'question' ? 'question' : 'holding'
  }

  /**
   * Scene 0's opening (a bundle `transition` on it) is a pre-roll too: while nothing has fired
   * yet, put the clock at `−lead` and let the opening play into the first line.
   */
  private prerollOpening(): void {
    if (this._sceneIndex !== 0 || this.fired.size > 0 || this.outroEnd !== null || this.clock.t > 0) return
    const lead = prerollOf(transitionFor(this.bundle, 0, 'start'))
    if (lead <= 0) return
    this._entry = 'start'
    this.clock.preroll(-lead)
  }

  /**
   * One frame. `dtMs` is the real elapsed time; `StoryClock` clamps it to
   * 100 ms for story time while the hold/idle timers use it raw (they are
   * wall-clock presentation timers, allowed by §8.1).
   */
  tick(dtMs: number): void {
    if (this.destroyed || this.scrubbing) return
    const dt = Number.isFinite(dtMs) && dtMs > 0 ? dtMs : 0

    if (this._phase === 'holding' || this._phase === 'question') {
      this.tickHold(dt)
      return
    }
    if (this._phase !== 'playing') return

    // The story's ending: story time runs on past the last scene until it has played.
    if (this.outroEnd !== null) {
      this.clock.advance(dt)
      if (this.clock.t >= this.outroEnd - EPS) {
        this.outroEnd = null
        this.finishStory()
      }
      return
    }

    const before = this.clock.t
    this.clock.advance(dt)
    let now = this.clock.t

    // Freeze exactly at the next unconsumed checkpoint (§8.1: `dur: 0`, the clock stops).
    const cp = this.nextCheckpoint(before)
    if (cp && cp.t <= now + EPS) {
      this.clock.reset(cp.t)
      now = cp.t
      this.fireEffects(before, now)
      this.enterHold(cp, true)
      return
    }

    this.fireEffects(before, now)

    // A scene with no `scene_end` at its end moves on by itself — once, not on every frame the
    // next scene takes to load.
    if (now >= this.scene.duration - EPS && !this.hasPendingCheckpoint() && !this.advancing) {
      void this.advanceScene()
    }
  }

  private hasPendingCheckpoint(): boolean {
    return this.cues.some((c) => c.op === 'checkpoint' && !this.passed.has(c.id) && c.t >= this.clock.t - EPS)
  }

  private nextCheckpoint(afterT: number): CheckpointCue | null {
    for (const c of this.cues) {
      if (c.op !== 'checkpoint') continue
      if (this.passed.has(c.id)) continue
      if (c.t < afterT - EPS) continue
      return c as CheckpointCue
    }
    return null
  }

  /** Fire cue effects once, forwards only. Anything in the past on a seek stays silent (§5.2). */
  private fireEffects(_from: number, to: number): void {
    for (const cue of this.cues) {
      if (cue.t > to + EPS) break
      if (this.fired.has(cue.id)) continue
      this.fired.add(cue.id)
      if (isLineCue(cue)) {
        this.startLine(cue, to - cue.t)
      } else if (cue.op === 'sfx') {
        this.emit('sfx', cue)
        if (!this.prefs.lowDistraction) this.audio?.sfx(cue.sound, cue.gain_db, Math.min(cue.dur ?? 2, this.scene.duration - cue.t))
      } else if (cue.op === 'music') {
        this.emit('music', cue)
        this.audio?.music(cue.track, cue.gain_db - (this.prefs.lowDistraction ? 8 : 0), cue.fade)
      }
    }
    // A line that has run out ends here, whether or not audio reported it.
    const active = this.activeLine
    if (active && to >= active.t + cueDur(active) - EPS) this.endLine(active)
  }

  private startLine(cue: LineCue, offsetS: number): void {
    if (this.activeLine && this.activeLine.id !== cue.id) this.endLine(this.activeLine)
    this.activeLine = cue
    this.emit('line', cue)
    this.playLineAudio(cue, offsetS)
    this.emit('state')
  }

  /** Start `cue`'s voice `offsetS` seconds in and hand it the clock. */
  private playLineAudio(cue: LineCue, offsetS: number): void {
    const port = this.audio
    if (!port) return
    void port.playLine(cue, Math.max(0, offsetS) * 1000)
    // Audio-master (§8.1): karaoke and talk clips cannot drift from the voice.
    const master: AudioMaster = { currentLineTimeMs: () => port.currentLineTimeMs() }
    this.clock.bindAudio(master, cue.t)
    this.lineAudible = true
  }

  /** Stop the voice but keep the line armed (pause, scrub). */
  private silenceLine(): void {
    this.audio?.stopLine()
    this.lineAudible = false
  }

  /**
   * Restart the armed line's voice at the clock's offset into it — after a
   * pause, a seek, a scrub or a scene jump. Idempotent and phase-aware: a line
   * that is already audible, a story that is not actually playing, and a drag
   * still in progress are all no-ops, so every path can simply call it.
   */
  private resumeLineAudio(): void {
    const line = this.activeLine
    if (!line || this.lineAudible || this.scrubbing || this.destroyed || this._phase !== 'playing') return
    this.playLineAudio(line, this.clock.t - line.t)
  }

  private endLine(cue: LineCue): void {
    if (this.activeLine?.id !== cue.id) return
    this.activeLine = null
    this.clock.bindAudio(null)
    this.silenceLine()
    this.emit('lineEnd', cue)
    this.emit('state')
  }

  /* ----------------------------------------------------------- the holds */

  /** Mode-dependent timings for a hold (§8.1, §8.6, ADDENDUM §5.3). */
  holdPolicyFor(kind: HoldKind, cue: CheckpointCue): HoldPolicy {
    const o = this.bundle.options
    if (this.scene.event_timing) {
      return { autoAfter: kind === 'scene_end' ? 0 : Math.min(6, Math.max(1, cue.auto_after ?? 3)), nudges: [], forceAfter: null }
    }
    const mode = this.prefs.mode
    if (kind === 'question') {
      // §8.6: a question waits for a tap in EVERY mode unless auto-answer is on.
      return { autoAfter: this.prefs.autoAnswer ? Math.max(6, o.question_auto_s) : null, nudges: [], forceAfter: null }
    }
    if (kind === 'tap_gate') {
      if (mode === 'tap') {
        return { autoAfter: null, nudges: this.prefs.tapGateNudgeS, forceAfter: this.prefs.tapGateForceS }
      }
      return { autoAfter: this.prefs.tapGateAutoS, nudges: [], forceAfter: null }
    }
    // scene_end: never expires in tap mode; the baked `auto_after` is only a hint.
    if (mode === 'tap') return { autoAfter: null, nudges: [], forceAfter: null }
    const auto = mode === 'bedtime' ? o.bedtime_hold_s : 0
    return { autoAfter: auto, nudges: [], forceAfter: null }
  }

  private enterHold(cue: CheckpointCue, continuous = false): void {
    this.holdCue = cue
    this.holdElapsedMs = 0
    this.holdNudged = 0
    // Continuous playback goes straight into the authored transition. No Next
    // overlay or extra dead-air timer between chapters (tap/bedtime stay opt-in).
    if (continuous && cue.kind === 'scene_end' && this.holdPolicyFor(cue.kind, cue).autoAfter === 0) {
      this.continueQueued = false
      this.releaseHold('auto')
      return
    }
    this.clock.pause()
    this._phase = this.holdPhase(cue.kind)
    this.emit('hold', cue.kind, cue)
    this.emit('state')
    // §8.6: one queued tap from the last 30 % of the line is consumed here.
    if (this.continueQueued) {
      this.continueQueued = false
      this.releaseHold('queued')
    }
  }

  private tickHold(dtMs: number): void {
    const cue = this.holdCue
    if (!cue) return
    this.holdElapsedMs += dtMs
    const sec = this.holdElapsedMs / 1000
    const policy = this.holdPolicyFor(cue.kind, cue)

    for (let i = this.holdNudged; i < policy.nudges.length; i += 1) {
      const at = policy.nudges[i] as number
      if (sec >= at) {
        this.holdNudged = i + 1
        this.emit('nudge', i, cue.kind)
      }
    }
    if (policy.forceAfter !== null && sec >= policy.forceAfter) {
      this.releaseHold('forced')
      return
    }
    if (policy.autoAfter !== null && sec >= policy.autoAfter) {
      this.releaseHold('auto')
      return
    }
    if (!this._dimmed && sec >= this.prefs.idleDimS) {
      this._dimmed = true
      this.emit('dim', true)
      this.emit('state')
    }
  }

  private releaseHold(how: 'tap' | 'auto' | 'forced' | 'queued'): void {
    const cue = this.holdCue
    if (!cue) return
    this.passed.add(cue.id)
    this.holdCue = null
    this.holdElapsedMs = 0
    this.holdNudged = 0
    this.undim()
    this.emit('holdRelease', cue.kind, how)

    if (cue.kind === 'scene_end' && this.clock.t >= this.scene.duration - EPS) {
      void this.advanceScene()
      return
    }
    this._phase = 'playing'
    this.clock.resume()
    this.syncMusic()
    this.emit('state')
  }

  private undim(): void {
    if (!this._dimmed) return
    this._dimmed = false
    this.emit('dim', false)
  }

  /* ------------------------------------------------------------- the tap */

  /**
   * The single entry point for stage taps and HUD buttons (§8.1). The policy
   * table, in order:
   *
   * | state                      | stage tap                    |
   * |----------------------------|------------------------------|
   * | within 300 ms of a tap     | `debounced`                  |
   * | dimmed                     | `undimmed` (wake, no continue)|
   * | holding / question         | `accepted` → continue        |
   * | playing, line last 30 %    | `queued` (at most one)       |
   * | playing, line or move      | `swallowed`                  |
   * | playing, anything else     | `swallowed`                  |
   * | waiting (next part coming) | `ignored`                    |
   * | paused / ended             | `ignored` (HUD buttons only) |
   */
  onTap(source: TapSource = 'stage'): TapResult {
    if (this.destroyed) return 'ignored'
    const now = this.nowMs()
    if (now - this.lastTapMs < TAP_DEBOUNCE_MS) return 'debounced'

    if (this._dimmed) {
      this.lastTapMs = now
      this.undim()
      this.holdElapsedMs = 0
      this.emit('state')
      return 'undimmed'
    }

    // Waiting for the next part to be made: a tap cannot bring it sooner.
    if (this._phase === 'waiting') return 'ignored'

    if (this._phase === 'holding' || this._phase === 'question') {
      this.lastTapMs = now
      this.releaseHold('tap')
      return 'accepted'
    }

    if (this._phase === 'playing') {
      const line = this.activeLine
      if (line) {
        const dur = cueDur(line)
        const tLocal = this.clock.t - line.t
        if (dur > 0 && tLocal >= dur * TAP_QUEUE_FRACTION) {
          if (!this.continueQueued) this.continueQueued = true
          return 'queued'
        }
        return 'swallowed'
      }
      if (this.activeMove()) return 'swallowed'
      // §8.1: no other cue kind is tappable.
      return 'swallowed'
    }

    // `paused` shows the pictures-only overlay and only its three buttons respond;
    // `ended` shows replay. A HUD button gets through, a stage tap does not.
    if (source === 'hud') {
      this.lastTapMs = now
      return 'accepted'
    }
    return 'ignored'
  }

  private activeMove(): Cue | null {
    const t = this.clock.t
    for (const cue of this.cues) {
      if (cue.t > t + EPS) break
      if (!isMoveCue(cue)) continue
      if (t < cue.t + cueDur(cue) - EPS) return cue
    }
    return null
  }

  /* --------------------------------------------------------------- seek */

  /**
   * Seek within the current scene. `seek(t)` then `snapshot()` equals playing to `t`. A seek is a
   * cut: the transition the scene was entered with is over (dragging the bar back to a part's start,
   * ↺ on its first line, never plays it again over the previous part's picture).
   */
  seek(t: number): void {
    this.audio?.stopSounds?.()
    const target = clamp(Number.isFinite(t) ? t : 0, 0, this.scene.duration)
    const wasHold = this._phase === 'holding' || this._phase === 'question'
    const wasWaiting = this._phase === 'waiting'
    // A seek lands in story time: a pre-roll or an ending in progress is simply over.
    this.outroEnd = null
    this._entry = 'jump'
    if (this.activeLine) this.endLine(this.activeLine)
    this.silenceLine()
    this.clock.reset(target)

    // Effects that already happened are "fired" (never replayed); everything after is armed again.
    this.fired = new Set(this.cues.filter((c) => c.t <= target + EPS).map((c) => c.id))
    this.passed = new Set(
      this.cues.filter((c) => c.op === 'checkpoint' && c.t < target - EPS).map((c) => c.id),
    )

    this.holdCue = null
    this.holdElapsedMs = 0
    this.holdNudged = 0
    this.continueQueued = false
    this.undim()

    // Landing exactly on an unreleased checkpoint means landing in its hold.
    const cp = this.cues.find((c) => c.op === 'checkpoint' && Math.abs(c.t - target) <= 1e-3 && !this.passed.has(c.id))
    if (cp) {
      this.enterHold(cp as CheckpointCue)
    } else if (this._phase === 'ended') {
      this._phase = 'paused'
    } else if (wasWaiting && this.pauseRequested) {
      // paused while waiting: the seek lands paused, like any seek from the pause overlay
      this.pauseRequested = false
      this._phase = 'paused'
    } else if (wasHold || wasWaiting) {
      // The hold this seek left is gone (`holdCue` was cleared above). Staying
      // in `holding` would strand the runner: no hold to release, a clock that
      // never resumes, and no tap that could ever get the child out. A seek
      // back into the last ready part while waiting plays on from there too.
      this._phase = 'playing'
      this.clock.resume()
    }

    // Re-arm the line the seek landed inside, at its offset (§8.1). The voice
    // starts only if the story is actually playing: a seek while paused must
    // not talk over the pause overlay, and `play()` picks the offset up later.
    const inside = this.lineAt(target)
    if (inside && !cp) {
      this.activeLine = inside
      this.emit('line', inside)
      this.resumeLineAudio()
    }

    this.emit('seek', target)
    this.syncMusic()
    this.emit('state')
  }

  private lineAt(t: number): LineCue | null {
    for (const cue of this.cues) {
      if (cue.t > t + EPS) break
      if (!isLineCue(cue)) continue
      if (t < cue.t + cueDur(cue) - EPS) return cue
    }
    return null
  }

  private lastLineCue(): LineCue | null {
    const t = this.clock.t
    let found: LineCue | null = null
    for (const cue of this.cues) {
      if (cue.t > t + EPS) break
      if (isLineCue(cue)) found = cue
    }
    return found
  }

  /** ↺ *again*: back to the start of the most recent line and keep going (§8.1). */
  replayLastLine(): boolean {
    // A "Next" is loading the next part: the last line is the old part's, about to be torn down.
    if (this.loadingScenes > 0) return false
    const cue = this.lastLineCue()
    if (!cue) return false
    this.seek(cue.t)
    this._phase = 'playing'
    this.clock.resume()
    this.resumeLineAudio()
    this.syncMusic()
    this.emit('state')
    return true
  }

  /* ------------------------------------------------------------- scenes */

  /**
   * Switch to scene `index`. `entry` (default `'jump'`) says how: a jump lands at `at` (0) as a
   * cut; a play-through or the story's start without an `at` starts the clock at the scene
   * transition's `−lead` instead (`seek(0)` would voice the t = 0 line at once, over the old picture).
   */
  async gotoScene(index: number, opts: { autoplay?: boolean; at?: number; entry?: SceneEntry } = {}): Promise<void> {
    if (this.destroyed) return
    const i = Math.round(index)
    if (i < 0) return this.gotoScene(0, opts)
    if (i >= this.bundle.scenes.length) {
      this.advancing = false
      // Past the last ready part of a story still being made ("Skip" on it): wait for the next.
      if (this.streamOpen) this.enterWaiting()
      else this.finishStory()
      return
    }
    const entry = opts.entry ?? 'jump'
    this.loadingScenes += 1
    try {
      if (this.loadScene) await this.loadScene(i, { entry })
    } finally {
      this.loadingScenes -= 1
      this.advancing = false
    }
    if (this.destroyed) return
    const stayPaused = this.pauseRequested
    this.pauseRequested = false

    this._sceneIndex = i
    // A part landed, so the wait is over: it lands paused, and plays below unless a pause was
    // pressed while the child waited.
    if (this._phase === 'waiting') {
      this._phase = 'paused'
      this.clock.pause()
    }
    this.rebuildCues()
    this.activeLine = null
    this.silenceLine()
    this.clock.bindAudio(null)
    const lead = entry !== 'jump' && opts.at === undefined ? prerollOf(transitionFor(this.bundle, i, entry)) : 0
    if (lead > 0) this.enterPreroll(lead)
    else this.seek(opts.at ?? 0)
    // after the landing seek (a seek is a cut): a play-through with no pre-roll still plays its
    // transition from t = 0, while one landing at an explicit `at` is a seek, so a cut
    this._entry = opts.at === undefined ? entry : 'jump'
    this.emit('sceneChange', i)
    if (opts.autoplay !== false && !stayPaused && this._phase !== 'holding' && this._phase !== 'question') {
      this._phase = 'playing'
      this.clock.resume()
    }
    // `seek` only voices a line when the story was already playing; a jump
    // from the pause overlay or from the end becomes "playing" just above.
    this.resumeLineAudio()
    this.syncMusic()
    this.emit('state')
  }

  /**
   * The clock at `−lead` with nothing fired, nothing passed and no line armed: the t = 0 cues
   * fire once, on the way through 0. A hold (or the end) this leaves is gone.
   */
  private enterPreroll(lead: number): void {
    if (this.activeLine) this.endLine(this.activeLine)
    this.silenceLine()
    this.fired = new Set()
    this.passed = new Set()
    this.activeLine = null
    this.holdCue = null
    this.holdElapsedMs = 0
    this.holdNudged = 0
    this.continueQueued = false
    this.outroEnd = null
    this.undim()
    this.clock.preroll(-lead)
    if (this._phase === 'holding' || this._phase === 'question' || this._phase === 'ended') {
      this._phase = 'paused'
      this.clock.pause()
    }
  }

  /**
   * "Next" (or a scene's end with no hold): the next scene by play-through; while the story is
   * still being made and the next part is not here yet, the wait for it; else the story's ending.
   */
  private async advanceScene(): Promise<void> {
    if (this.advancing || this.destroyed) return
    if (this._sceneIndex + 1 < this.bundle.scenes.length) {
      // cleared by `gotoScene` the moment the load settles, so a synchronous load can advance again
      this.advancing = true
      await this.gotoScene(this._sceneIndex + 1, { autoplay: true, entry: 'play' })
      return
    }
    if (this.streamOpen) {
      this.enterWaiting()
      return
    }
    const ending = endingFor(this.bundle, this._sceneIndex)
    if (ending && ending.dur > 0 && this.outroEnd === null) {
      // The ending plays in story time past the last scene's end, then the story is over.
      this.outroEnd = this.scene.duration + ending.dur
      this._phase = 'playing'
      this.clock.resume()
      this.emit('state')
      return
    }
    this.finishStory()
  }

  /**
   * The end. A line still armed here (skipping past the last part mid-sentence)
   * is ended, not just silenced — otherwise its words sit in the subtitle band
   * under "Again!" for as long as the child looks at it.
   */
  private finishStory(): void {
    this.audio?.stopSounds?.()
    this.audio?.music(null, undefined, 1)
    this.outroEnd = null
    this._phase = 'ended'
    // A pause pressed during the wait for a part that never came ("End here", a failed or deleted
    // story) has nothing left to hold: "From the start" or a jump from the end screen plays. One
    // pressed during a load still in flight stays for that load's landing.
    if (this.loadingScenes === 0) this.pauseRequested = false
    this.clock.pause()
    if (this.activeLine) this.endLine(this.activeLine)
    this.silenceLine()
    this.emit('ended')
    this.emit('state')
  }

  /* ----------------------------------------------------- a story being made */

  /**
   * The end of the last ready part while more are coming: the clock stops on the frame it is on
   * (the next part's transition carries it away), the line is ended so no words hang in the band,
   * and a hold this leaves (a "Skip" from a question) is gone. Clears `advancing`: the next
   * `scenesAppended()` must be able to advance.
   */
  private enterWaiting(): void {
    this.audio?.pauseBackground?.(true)
    this.audio?.stopSounds?.()
    this.advancing = false
    this.outroEnd = null
    if (this.activeLine) this.endLine(this.activeLine)
    this.silenceLine()
    this.holdCue = null
    this.holdElapsedMs = 0
    this.holdNudged = 0
    this.continueQueued = false
    this.undim()
    this.clock.pause()
    this._phase = 'waiting'
    this.emit('state')
  }

  /**
   * Whether more parts are coming (`stream.ts#expectsMore`, told by the player on every revision).
   * Closing it while the child waits goes on to whatever did arrive — a final bundle's new parts —
   * or ends the story there; closing it earlier ends the story at the last ready part's end.
   */
  setStreamOpen(open: boolean): void {
    if (this.destroyed) return
    const was = this.streamOpen
    this.streamOpen = open
    if (open || !was) return
    if (this.leavesWaitNow()) void this.advanceScene()
  }

  /**
   * The wait is ours to end: the runner is in it, and no scene load is in flight. A grown-up's jump
   * out of the wait (the part before, this part again, a seek) stays `waiting` until it lands; a
   * second load started under it would race it. Once it lands, the runner is out of the wait and
   * the ordinary end of that part goes on to what arrived (or to the end).
   */
  private leavesWaitNow(): boolean {
    return this._phase === 'waiting' && !this.advancing && this.loadingScenes === 0
  }

  /** The bundle grew (the player appended scenes in place): a child waiting at the end plays on. */
  scenesAppended(): void {
    if (this.destroyed) return
    if (this.leavesWaitNow() && this._sceneIndex + 1 < this.bundle.scenes.length) {
      void this.advanceScene()
      return
    }
    // `sceneCount` changed: the HUD's seek bar and part count follow
    this.emit('state')
  }

  skipScene(): Promise<void> {
    return this.gotoScene(this._sceneIndex + 1, { autoplay: true })
  }

  previousScene(): Promise<void> {
    return this.gotoScene(Math.max(0, this._sceneIndex - 1), { autoplay: true })
  }

  replayScene(): Promise<void> {
    return this.gotoScene(this._sceneIndex, { autoplay: true, at: 0 })
  }

  destroy(): void {
    this.destroyed = true
    this.clock.pause()
    this.silenceLine()
    this.audio?.stopSounds?.()
    this.audio?.music(null, undefined, 0)
    this.listeners.clear()
  }
}
